import { Dispatch, SetStateAction, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Pencil from "chameleon/icons/pencil";
import Trash from "chameleon/icons/trash";
import CloseIcon from "chameleon/icons/x";
import { Button } from "chameleon/lib/components";
import { Flyout } from "chameleon/lib/components/flyout/flyout";
import { MenuItem } from "chameleon/lib/components/flyout/menu-item";
import HelpTooltip from "chameleon/lib/components/help-tooltip/help-tooltip";
import { RadioOption } from "chameleon/lib/components/radio-group/radio-group";

import { RADIO_GROUP_PAYMENT_METHODS_TYPE } from "checkout-experience/components/payment-method/constants";
import PaymentMethodSummaryDisplay from "checkout-experience/components/payment-method/payment-method-summary-display";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import {
  getPaymentTypeOfSavedPaymentMethods,
  isPaymentMethodExpired,
} from "checkout-experience/utils/payment-helpers";
import { useCheckoutSession } from "checkout-query-client/hooks/use-checkout-session";
import { useDeleteSavedPaymentMethod } from "checkout-query-client/hooks/use-delete-saved-payment-method";
import { usePaymentForm } from "checkout-query-client/hooks/use-form";
import { ChexFieldValues } from "checkout-query-client/types/chex-form-fields";
import { PaymentType } from "checkout-query-client/types/form-types";

import { CardSummary, PaymentGatewayType } from "frontend-client";

import { getIsFeatureEnabled } from "splitio-loader/src/utils/get-split-io-flag";

import { useEnabledSavedPaymentMethods } from "./use-enabled-saved-payment-methods";

export const useOnChangeSavedPaymentType = (
  editFormId: string | undefined,
  setEditFormId: Dispatch<SetStateAction<string | undefined>>
): ((value: string) => void) => {
  const { paymentOption, setPaymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "" } = paymentOption ?? {};
  const {
    checkoutSessionToken = "",
    apiHost = "",
    version = "",
  } = useChexContext();

  const { data: checkoutSession } = useCheckoutSession({
    apiHost,
    version,
    checkoutSessionToken,
  });
  const { paymentMethods } = checkoutSession ?? {};

  return (value: string) => {
    if (value !== formId && paymentMethods) {
      const index = paymentMethods.findIndex(
        (paymentMethod) => paymentMethod.paymentMethodId === value
      );

      if (index !== undefined && paymentMethods[index]) {
        const paymentType = getPaymentTypeOfSavedPaymentMethods(
          paymentMethods[index]
        );
        setPaymentOption?.({
          formId: value,
          paymentType,
        });

        if (
          editFormId &&
          editFormId === paymentMethods[index].paymentMethodId
        ) {
          setEditFormId(undefined);
        }
      }
    }
  };
};

export const useEditPayResetAnimation = (editFormId: string): string => {
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "", paymentType = PaymentType.creditCard } =
    paymentOption ?? {};

  const {
    merchantSettings,
    checkoutSessionToken = "",
    apiHost = "",
    version = "",
  } = useChexContext();

  const { data: checkoutSession } = useCheckoutSession({
    apiHost,
    version,
    checkoutSessionToken,
  });
  const { data: formConfig } = usePaymentForm({
    checkoutSession,
    merchantSettings,
    paymentType,
    formId,
    checkoutSessionToken,
  });

  return useMemo(() => {
    const activeFieldsLength = Object.values(formConfig?.fields || {}).filter(
      (field) => field?.active
    ).length;
    return `${activeFieldsLength}_${editFormId}_${formId}`;
  }, [formConfig?.fields, editFormId, formId]);
};

export const useSavedPaymentMethodOptions = (
  editFormId: string,
  onCancelEditPaymentMethod: () => void,
  onEditPaymentMethod: (id: string) => void,
  surchargeText?: string
): RadioOption[] => {
  const { t } = useTranslation(["combined"]);
  const paymentMethods = useEnabledSavedPaymentMethods() ?? [];
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "" } = paymentOption ?? {};
  const { watch } = useFormContext() ?? {};
  const formFields = watch(`${paymentOption?.formId}`) as ChexFieldValues;
  const {
    checkoutSessionToken = "",
    apiHost = "",
    version = "",
    merchantSettings,
    target = "",
  } = useChexContext();
  const { mutateAsync: deletePaymentMethod, isPending: isDeleting } =
    useDeleteSavedPaymentMethod(checkoutSessionToken, apiHost, version);
  const { refetch: refetchSession } = useCheckoutSession({
    apiHost,
    version,
    checkoutSessionToken,
  });

  const onClickFlyoutEdit = (paymentMethodId: string) => () => {
    onEditPaymentMethod(paymentMethodId);
  };

  const onClickFlyoutDelete = (paymentMethodId: string) => () => {
    void deletePaymentMethod(paymentMethodId).then(() => {
      void refetchSession();
    });
  };

  return useMemo(() => {
    return (
      paymentMethods.map((paymentMethod, index) => {
        const isCancel =
          editFormId === paymentMethod.paymentMethodId &&
          editFormId === paymentOption?.formId;
        const { expirationMonth, expirationYear } = paymentMethod.paymentMethod
          .value as CardSummary;

        /** This isExpired is checking for every list item in saved MOP list. that's why its not using useIsExpired.  */
        const isExpired = isPaymentMethodExpired({
          expirationMonth: expirationMonth,
          expirationYear: expirationYear,
        });

        const paymentMethodType =
          getPaymentTypeOfSavedPaymentMethods(paymentMethod);
        const summary = {
          ...paymentMethod.paymentMethod.value,
          paymentType: paymentMethodType,
        };
        // default items from UPG is assumed to be the first and can't be deleted.
        const isUPGDefaultMOP = () =>
          merchantSettings?.supportedPaymentGatewayType ===
            PaymentGatewayType.UPG && index === 0;
        return {
          id: paymentMethod.paymentMethodId,
          type: RADIO_GROUP_PAYMENT_METHODS_TYPE.savedPaymentMethods,
          messages:
            paymentMethod.paymentMethodId === paymentOption?.formId &&
            surchargeText
              ? [surchargeText]
              : [],
          labelComponent: paymentMethod.paymentMethod.value ? (
            <PaymentMethodSummaryDisplay
              paymentMethodSummary={summary}
              isExpired={isExpired}
              isCollapsed={!isCancel}
              titleStyleVariant={"heading4"}
              forId={`${paymentMethod.paymentMethodId}Option`}
              trailingSlot={
                <div>
                  {!isCancel &&
                  !isExpired &&
                  getIsFeatureEnabled(target, "q3-bank-account-profiles") ? (
                    <Flyout
                      ariaLabel={t("moreActions") ?? ""}
                      optionsArray={[
                        <MenuItem
                          testId={"flyout-edit-btn"}
                          key={"edit"}
                          text={t("savedPaymentMethod.edit")}
                          onClick={onClickFlyoutEdit(
                            paymentMethod.paymentMethodId
                          )}
                          leftSvg={
                            <Pencil
                              role={"presentation"}
                              aria-hidden={"true"}
                            />
                          }
                          isDisabled={isDeleting}
                        />,
                        <MenuItem
                          testId={"flyout-delete-btn"}
                          key={"delete"}
                          text={t("savedPaymentMethod.delete")}
                          onClick={onClickFlyoutDelete(
                            paymentMethod.paymentMethodId
                          )}
                          variant={"critical"}
                          leftSvg={
                            <Trash role={"presentation"} aria-hidden={"true"} />
                          }
                          isDisabled={isDeleting || isUPGDefaultMOP()}
                          rightSvg={
                            isUPGDefaultMOP() ? (
                              <div
                                data-testid={"flyout-delete-btn-help-tooltip"}
                              >
                                <HelpTooltip
                                  content={t("savedPaymentMethod.cannotDelete")}
                                  position={"left"}
                                  tooltipAriaLabel={t("tooltipAriaLabel")}
                                />
                              </div>
                            ) : undefined
                          }
                        />,
                      ]}
                    />
                  ) : (
                    <Button
                      id="saved-payment-edit-btn"
                      variant="text"
                      testId="btn-edit-payment-method" // Do not change the test-id as it is linked with keyboard navigation
                      text={
                        isCancel
                          ? t("savedPaymentMethod.cancel")
                          : isExpired
                            ? t("savedPaymentMethod.update")
                            : t("savedPaymentMethod.edit")
                      }
                      tabIndex={-1}
                      onClick={() => {
                        if (isCancel) {
                          onCancelEditPaymentMethod();
                        } else {
                          onEditPaymentMethod(paymentMethod.paymentMethodId);
                        }
                      }}
                      position="left"
                      svg={
                        isCancel ? <CloseIcon sizeVariant={24} /> : undefined
                      }
                    />
                  )}
                </div>
              }
            />
          ) : (
            <></>
          ),
        };
      }) || []
    );
  }, [paymentMethods, formId, editFormId, formFields]);
};

const usePaymentMethodIndex = () => {
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "" } = paymentOption ?? {};
  const paymentMethods = useEnabledSavedPaymentMethods() ?? [];

  return useMemo(
    () =>
      paymentMethods?.findIndex(
        (paymentMethod) => paymentMethod.paymentMethodId === formId
      ),
    [paymentMethods, formId]
  );
};

export const useIsSavedPaymentMethodExpired = (): boolean => {
  const {
    apiHost = "",
    version = "",
    checkoutSessionToken = "",
  } = useChexContext();

  const { data: checkoutSession } = useCheckoutSession({
    apiHost,
    version,
    checkoutSessionToken,
  });

  const { paymentMethods } = checkoutSession ?? {};
  const paymentMethodIndex = usePaymentMethodIndex() ?? -1;

  return useMemo(
    () =>
      paymentMethodIndex > -1 && paymentMethods?.[paymentMethodIndex]
        ? isPaymentMethodExpired(
            paymentMethods[paymentMethodIndex].paymentMethod
              .value as CardSummary
          )
        : false,
    [checkoutSession, paymentMethods, paymentMethodIndex]
  );
};

export const useHideSetAsDefault = (): boolean => {
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { paymentType = PaymentType.creditCard } = paymentOption ?? {};
  const {
    merchantSettings,
    apiHost = "",
    version = "",
    checkoutSessionToken = "",
  } = useChexContext();
  const { data: checkoutSession } = useCheckoutSession({
    apiHost,
    version,
    checkoutSessionToken,
  });

  const { saveConsumerProfile } = checkoutSession ?? {};

  const paymentMethodIndex = usePaymentMethodIndex();

  return useMemo(
    () => paymentMethodIndex === 0 || !saveConsumerProfile,
    [
      checkoutSession,
      paymentMethodIndex,
      merchantSettings?.supportedPaymentGatewayType,
      paymentType,
      saveConsumerProfile,
    ]
  );
};
